/**
 * TeachingCoPilot — Pro+/School-gated chat widget that lives directly below the
 * Smart Insights panel. Free/Starter users see a blurred preview with an
 * Upgrade-to-Pro CTA. Pro/Elite/School users get the live chat.
 *
 * Capabilities:
 *   • Multi-turn chat with platform context (board, class, subject, topic)
 *   • File upload (jpg/png/pdf/docx/txt/pptx) for OCR / extraction
 *   • Structured response — Explanation / Key Points / Teaching Tip
 *   • Export current response as DOCX or PDF (AI-processed output only)
 *   • Suggested prompts + follow-up refinement
 */
import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bot, Crown, Lock, Loader2, Paperclip, Send, Sparkles, X,
  FileText, FileDown, Lightbulb, ListChecks, RefreshCw, ImageIcon,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { contentEngineApi } from '../../services/contentEngineApi';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const SUGGESTED_PROMPTS = [
  { label: 'Explain this simply', text: 'Explain this simply for my class.' },
  { label: 'Summarize this file', text: 'Summarize the uploaded file in key points.' },
  { label: 'Create questions from this', text: 'Generate 5 short revision questions from this content.' },
  { label: 'Give teaching tips', text: 'Give me teaching tips to introduce this topic to students.' },
];

const ACCEPTED_TYPES = '.jpg,.jpeg,.png,.webp,.pdf,.docx,.txt,.pptx';

const PRO_TIERS = new Set(['pro', 'elite', 'school_pro']);

export function TeachingCoPilot({ access, context }) {
  const tier = (access?.tier || 'free').toLowerCase();
  const isLocked = !PRO_TIERS.has(tier);
  if (isLocked) return <CoPilotLocked tier={tier} />;
  return <CoPilotChat context={context} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// Locked state — blurred preview + Upgrade CTA
// ─────────────────────────────────────────────────────────────────────────────
function CoPilotLocked({ tier }) {
  const dispatchUpgrade = () => {
    window.dispatchEvent(new CustomEvent('ce:open-upgrade', {
      detail: {
        feature: 'TEACHING_COPILOT',
        message: 'Teaching Co-Pilot is part of the Pro plan.',
        currentTier: tier,
        requiredTier: 'pro',
      },
    }));
  };
  return (
    <div
      data-testid="copilot-locked"
      className="relative rounded-2xl border border-slate-200/60 dark:border-slate-800 bg-gradient-to-br from-slate-50 to-white dark:from-slate-900 dark:to-slate-950 shadow-soft overflow-hidden"
    >
      {/* Blurred ghost preview */}
      <div className="p-4 blur-sm select-none pointer-events-none" aria-hidden="true">
        <div className="flex items-center gap-2 mb-3">
          <div className="h-8 w-8 rounded-lg bg-indigo-100 flex items-center justify-center">
            <Bot className="h-4 w-4 text-indigo-600" />
          </div>
          <div>
            <div className="text-sm font-semibold">Ask Bosco</div>
            <div className="text-[11px] text-muted-foreground">Ask · Upload · Refine</div>
          </div>
        </div>
        <div className="space-y-2">
          <div className="h-3 w-3/4 rounded bg-slate-200 dark:bg-slate-800" />
          <div className="h-3 w-2/3 rounded bg-slate-200 dark:bg-slate-800" />
          <div className="h-3 w-1/2 rounded bg-slate-200 dark:bg-slate-800" />
        </div>
        <div className="mt-3 flex gap-1.5">
          {SUGGESTED_PROMPTS.slice(0, 3).map((p) => (
            <span key={p.label} className="text-[10px] px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500">
              {p.label}
            </span>
          ))}
        </div>
      </div>
      {/* Overlay */}
      <div className="absolute inset-0 z-10 flex flex-col items-center justify-center text-center p-5 bg-white/60 dark:bg-slate-950/60 backdrop-blur-[2px]">
        <div className="h-10 w-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mb-2">
          <Lock className="h-4 w-4 text-amber-700 dark:text-amber-300" />
        </div>
        <div className="text-sm font-semibold flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5 text-amber-500" />
          Ask Bosco
        </div>
        <p className="text-[11px] text-muted-foreground mt-1 max-w-[260px] leading-relaxed">
          Unlock <strong className="text-foreground">Ask Bosco</strong> to get instant explanations,
          file understanding, and teaching assistance — right next to your work.
        </p>
        <Button
          size="sm"
          onClick={dispatchUpgrade}
          data-testid="copilot-upgrade-cta"
          className="mt-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-md h-8 text-xs"
        >
          <Crown className="h-3.5 w-3.5 mr-1.5" /> Upgrade to Pro
        </Button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Live chat (Pro+/School)
// ─────────────────────────────────────────────────────────────────────────────
function CoPilotChat({ context }) {
  const [messages, setMessages] = useState([]);   // {role:'user'|'assistant', content?, structured?, ts}
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [attachment, setAttachment] = useState(null); // {fileToken, filename, kind, summary}
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);
  const sessionId = useMemo(
    () => `copilot-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    [],
  );

  // Auto-scroll to bottom on new message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const onPickFile = () => fileInputRef.current?.click();

  const onFileChange = useCallback(async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('File too large — max 8 MB.');
      return;
    }
    setUploading(true);
    try {
      const res = await contentEngineApi.copilotUpload(file);
      setAttachment({
        fileToken: res.fileToken,
        filename: res.filename || file.name,
        kind: res.kind,
        summary: res.summary,
      });
      toast.success(`Attached: ${res.filename || file.name}`);
    } catch (err) {
      toast.error(err?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, []);

  const send = useCallback(async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || loading) return;
    const userMsg = { role: 'user', content: text, ts: Date.now(), attachment };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    try {
      const history = [...messages, userMsg]
        .slice(-6)
        .map((m) => ({
          role: m.role,
          content: m.role === 'assistant' && m.structured
            ? `${m.structured.explanation}\n${(m.structured.keyPoints || []).map((p) => `- ${p}`).join('\n')}`
            : (m.content || ''),
        }));
      const payload = {
        message: text,
        history: history.slice(0, -1), // exclude current user msg (server takes it as message)
        context: {
          classId: context?.classId || null,
          subject: context?.subject || null,
          curriculum: context?.curriculum || null,
          topic: context?.topic || null,
        },
        sessionId,
        fileToken: attachment?.fileToken || null,
      };
      const res = await contentEngineApi.copilotChat(payload);
      const assistantMsg = {
        role: 'assistant',
        structured: {
          explanation: res.explanation || '',
          keyPoints: res.keyPoints || [],
          teachingTip: res.teachingTip || '',
        },
        followUp: res.followUp,
        ts: Date.now(),
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch (err) {
      if (err?.code === 'UPGRADE_REQUIRED') {
        window.dispatchEvent(new CustomEvent('ce:open-upgrade', { detail: err.detail }));
      } else {
        toast.error(err?.message || 'Co-Pilot failed');
      }
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, attachment, context, sessionId]);

  const onExport = useCallback(async (format, msg) => {
    try {
      const titleBits = [context?.subject, context?.topic].filter(Boolean);
      const title = titleBits.length ? `Co-Pilot — ${titleBits.join(' · ')}` : 'Teaching Co-Pilot';
      await contentEngineApi.copilotExport({
        format,
        title,
        explanation: msg.structured?.explanation || '',
        keyPoints: msg.structured?.keyPoints || [],
        teachingTip: msg.structured?.teachingTip || '',
      });
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (err) {
      toast.error(err?.message || 'Export failed');
    }
  }, [context]);

  const reset = () => {
    setMessages([]);
    setAttachment(null);
    setInput('');
  };

  const isEmpty = messages.length === 0;

  return (
    <div
      data-testid="teaching-copilot"
      className="relative rounded-2xl border border-slate-200/60 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-soft overflow-hidden flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200/60 dark:border-slate-800">
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white shadow-sm shrink-0">
            <Bot className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold flex items-center gap-1.5 truncate">
              Ask Bosco
              <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-900/50">PRO</Badge>
            </div>
            <div className="text-[10px] text-muted-foreground truncate">Your teaching co-pilot. Ask, upload, refine.</div>
          </div>
        </div>
        <button
          onClick={reset}
          data-testid="copilot-reset"
          title="Start a new chat"
          className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-md border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors shrink-0"
        >
          <RefreshCw className="h-3 w-3" /> New
        </button>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="px-4 py-3 max-h-[360px] min-h-[120px] overflow-y-auto space-y-3 text-sm"
        data-testid="copilot-messages"
      >
        {isEmpty && (
          <div className="text-center py-2">
            <div className="text-xs text-muted-foreground mb-2">Ask me anything about today's topic.</div>
            <div className="flex flex-wrap gap-1.5 justify-center">
              {SUGGESTED_PROMPTS.map((p) => (
                <button
                  key={p.label}
                  onClick={() => send(p.text)}
                  data-testid={`copilot-suggested-${p.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="text-[11px] px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        )}
        <AnimatePresence initial={false}>
          {messages.map((msg, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.18 }}
              className={cn(
                'flex',
                msg.role === 'user' ? 'justify-end' : 'justify-start',
              )}
            >
              {msg.role === 'user' ? (
                <UserBubble msg={msg} />
              ) : (
                <AssistantBubble msg={msg} onExport={onExport} />
              )}
            </motion.div>
          ))}
        </AnimatePresence>
        {loading && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="copilot-loading">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            <span>Bosco is thinking…</span>
          </div>
        )}
      </div>

      {/* Attachment chip */}
      {attachment && (
        <div className="px-4 pt-2" data-testid="copilot-attachment">
          <div className="flex items-center justify-between gap-2 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-900/40 px-2.5 py-1.5 text-[11px]">
            <div className="flex items-center gap-1.5 min-w-0">
              {attachment.kind === 'image' ? (
                <ImageIcon className="h-3.5 w-3.5 text-indigo-600 shrink-0" />
              ) : (
                <FileText className="h-3.5 w-3.5 text-indigo-600 shrink-0" />
              )}
              <span className="font-medium truncate text-indigo-900 dark:text-indigo-200">{attachment.filename}</span>
              <span className="text-indigo-700/70 dark:text-indigo-300/70 truncate hidden sm:inline">· {attachment.summary}</span>
            </div>
            <button
              onClick={() => setAttachment(null)}
              data-testid="copilot-remove-attachment"
              className="text-indigo-700 dark:text-indigo-300 hover:text-indigo-900 shrink-0"
              title="Remove attachment"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="px-4 py-3 border-t border-slate-200/60 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
        <div className="flex items-end gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES}
            onChange={onFileChange}
            className="hidden"
            data-testid="copilot-file-input"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onPickFile}
            disabled={uploading || loading}
            className="h-9 w-9 shrink-0"
            data-testid="copilot-upload-btn"
            title="Attach file (jpg, png, pdf, docx, txt, pptx)"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
          </Button>
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            disabled={loading}
            placeholder="Ask Bosco…"
            data-testid="copilot-input"
            className="flex-1 min-w-0 h-9 text-sm"
          />
          <Button
            type="button"
            onClick={() => send()}
            disabled={loading || !input.trim()}
            className="h-9 px-3 bg-indigo-600 hover:bg-indigo-700 text-white shrink-0 gap-1.5"
            data-testid="copilot-send"
            title="Fetch (Enter)"
          >
            <Send className="h-4 w-4" />
            <span className="text-xs font-semibold">Fetch</span>
          </Button>
        </div>
        <div className="mt-1.5 text-[10px] text-muted-foreground text-center">
          Responses are AI-generated and meant as a teaching aid.
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Bubbles
// ─────────────────────────────────────────────────────────────────────────────
function UserBubble({ msg }) {
  return (
    <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-indigo-600 text-white px-3 py-2 text-[13px] shadow-sm" data-testid="copilot-user-msg">
      {msg.attachment && (
        <div className="mb-1 flex items-center gap-1 text-[10px] text-indigo-100">
          {msg.attachment.kind === 'image' ? <ImageIcon className="h-3 w-3" /> : <FileText className="h-3 w-3" />}
          <span className="truncate">{msg.attachment.filename}</span>
        </div>
      )}
      <div className="leading-snug whitespace-pre-wrap break-words">{msg.content}</div>
    </div>
  );
}

function AssistantBubble({ msg, onExport }) {
  const s = msg.structured || {};
  const hasContent = s.explanation || (s.keyPoints && s.keyPoints.length) || s.teachingTip;
  return (
    <div className="max-w-[92%] rounded-2xl rounded-tl-sm bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100 px-3 py-2.5 text-[13px] shadow-sm space-y-2" data-testid="copilot-assistant-msg">
      {s.explanation && (
        <div>
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-indigo-700 dark:text-indigo-300 font-semibold mb-0.5">
            <FileText className="h-3 w-3" /> Explanation
          </div>
          <div className="leading-snug">{s.explanation}</div>
        </div>
      )}
      {s.keyPoints?.length > 0 && (
        <div>
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-indigo-700 dark:text-indigo-300 font-semibold mb-0.5">
            <ListChecks className="h-3 w-3" /> Key Points
          </div>
          <ul className="space-y-0.5 ml-3 list-disc marker:text-slate-400 leading-snug">
            {s.keyPoints.map((p, i) => <li key={i}>{p}</li>)}
          </ul>
        </div>
      )}
      {s.teachingTip && (
        <div>
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-amber-700 dark:text-amber-300 font-semibold mb-0.5">
            <Lightbulb className="h-3 w-3" /> Teaching Tip
          </div>
          <div className="leading-snug">{s.teachingTip}</div>
        </div>
      )}
      {hasContent && (
        <div className="pt-2 mt-1 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between gap-2">
          <span className="text-[10px] text-muted-foreground italic truncate">
            {msg.followUp || 'Is this helpful, or would you like me to refine it further?'}
          </span>
          <div className="flex gap-1 shrink-0">
            <button
              onClick={() => onExport('docx', msg)}
              data-testid="copilot-export-docx"
              className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 hover:border-indigo-300 hover:text-indigo-700 transition-colors"
              title="Export as DOCX"
            >
              <FileDown className="h-3 w-3" /> DOCX
            </button>
            <button
              onClick={() => onExport('pdf', msg)}
              data-testid="copilot-export-pdf"
              className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 hover:border-indigo-300 hover:text-indigo-700 transition-colors"
              title="Export as PDF"
            >
              <FileDown className="h-3 w-3" /> PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
